import { MitigationResolutionTabComponent } from './../components/report-form/tabs/mitigation-resolution-tab/mitigation-resolution-tab.component';
import { computed, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ApprovalState, ReportType } from '@fdm/api-client';
import { AuthService, CommonUserService, CurrentUserService, Role } from '@fdm/services';
import { ApprovalFlowService } from './approval-flow.service';
import { ReportFormStore, StageField } from './report-form.store';

@Injectable({
  providedIn: 'root',
})
export class ButtonScriptsService {
  private _userService = inject(CurrentUserService);
  private _common_scripts = inject(CommonUserService); //common scripts
  private _store = inject(ReportFormStore); //general tab id
  private _approvalFlowServie = inject(ApprovalFlowService); //step value
  private _authService = inject(AuthService);

  isAdmin = this._authService.isInRole(Role.Admin);
  isCreator = this._authService.isInRole(Role.Creator);
  isRiptRisk = this._authService.isInRole(Role.RIPTRisk);
  isReadOnly = this._authService.isInRole(Role.Readonly);


  approvalFlowState = toSignal(this._store.approvalFlow$);

  hasLTAApproved = computed(() => !!this.approvalFlowState()?.hasLTAApproved);
  hasHCEPOApproved = computed(() => !!this.approvalFlowState()?.hasHCEPOApproved);
  hasAreaLeadApproved = computed(() => !!this.approvalFlowState()?.hasAreaLeadApproved);
  hasAreaOpsApproved = computed(() => !!this.approvalFlowState()?.hasAreaOpsApproved);
  hasProdOpManagerApproved = computed(() => !!this.approvalFlowState()?.hasProdOpManagerApproved);
  hasGenManagerOperatorApproved = computed(() => !!this.approvalFlowState()?.hasGenManagerOperatorApproved);
  hasDirectorApproved = computed(() => !!this.approvalFlowState()?.hasDirectorApproved);

  general = toSignal(this._store.general$);
  abcPrioritization = toSignal(this._store.abcPrioritization$);
  authLevel = toSignal(this._store.assignedManagementAuthorizationLevel$);
  ript = toSignal(this._store.ript$);
  userEmails = toSignal(this._common_scripts.userEmails$);
  roles = toSignal(this._authService.roles$);


  currentApprovalState = computed(() => this.general()?.currentState);

  isAreaLeadValid = computed(() => {
    const tab = this.approvalFlowState();
    const emails = this.userEmails();
    const isReadOnly = this._authService.isReadOnly();

    if (isReadOnly) {
      return false;
    }

    return tab?.areaLeadInspEng && emails
      ? this._common_scripts.matchesWithField(tab?.areaLeadInspEng)
      : false;
  })

  isInitiatorValid = computed(() => {
    const initiator = this.general()?.initiator;
    const emails = this.userEmails();
    const isReadOnly = this._authService.isReadOnly();

    if (isReadOnly) {
      return false;
    }

    return initiator && emails
      ? this._common_scripts.matchesWithField(initiator)
      : false;
  })

  // Sr Ops Consultant -> Operation advisor
  isSrOpsConsultantValid = computed(() => {
    const tab = this.approvalFlowState();
    const emails = this.userEmails();
    const isReadOnly = this._authService.isReadOnly();

    if (isReadOnly) {
      return false;
    }

    return tab?.srOpsConsultant && emails
      ? this._common_scripts.matchesWithField(tab?.srOpsConsultant)
      : false;
  })

  isInspEngSupervisorValid = computed(() => {
    const tab = this.approvalFlowState();
    const emails = this.userEmails();
    const isReadOnly = this._authService.isReadOnly();

    if (isReadOnly) {
      return false;
    }

    return tab?.inspEngSupervisor && emails
      ? this._common_scripts.matchesWithField(tab?.inspEngSupervisor)
      : false;
  });

  isThreatOwnerValid = computed(() => {
    const general = this.general();
    const emails = this.userEmails();
    const isReadOnly = this._authService.isReadOnly();

    if (isReadOnly) {
      return false;
    }

    return general?.owner && emails
      ? this._common_scripts.matchesWithField(general.owner)
      : false;
  })

  // LTA
  isLocalTechnicalAuthorityValid = computed(() => {
    const tab = this.approvalFlowState();
    const emails = this.userEmails();
    const isReadOnly = this._authService.isReadOnly();

    if (isReadOnly) {
      return false;
    }

    return tab?.localTechnicalAuthority && emails
      ? this._common_scripts.matchesWithField(tab?.localTechnicalAuthority)
      : false;
  });

  isAssetTypeHCEPOValid = computed(() => {
    const tab = this.approvalFlowState();
    const emails = this.userEmails();
    const isReadOnly = this._authService.isReadOnly();

    if (isReadOnly) {
      return false;
    }

    return tab?.assetTypeHCEPO && emails
      ? this._common_scripts.matchesWithField(tab?.assetTypeHCEPO)
      : false;
  });

  isAreaOpsSuperintendentValid = computed(() => {
    const tab = this.approvalFlowState();
    const emails = this.userEmails();
    const isReadOnly = this._authService.isReadOnly();

    if (isReadOnly) {
      return false;
    }

    return tab?.areaOpsSuperintendent && emails
      ? this._common_scripts.matchesWithField(tab?.areaOpsSuperintendent)
      : false;
  });

  isProductionOperationManagerValid = computed(() => {
    const tab = this.approvalFlowState();
    const emails = this.userEmails();
    const isReadOnly = this._authService.isReadOnly();

    if (isReadOnly) {
      return false;
    }

    return tab?.productionOperationManager && emails
      ? this._common_scripts.matchesWithField(tab?.productionOperationManager)
      : false;
  });

  isGeneralManagerOperationValid = computed(() => {
    const tab = this.approvalFlowState();
    const emails = this.userEmails();
    const isReadOnly = this._authService.isReadOnly();

    if (isReadOnly) {
      return false;
    }

    return tab?.generalManagerOperation && emails
      ? this._common_scripts.matchesWithField(tab?.generalManagerOperation)
      : false;
  });

  isGeneralDirectorOperationValid = computed(() => {
    const tab = this.approvalFlowState();
    const emails = this.userEmails();
    const isReadOnly = this._authService.isReadOnly();

    if (isReadOnly) {
      return false;
    }

    return tab?.generalDirectorOperation && emails
      ? this._common_scripts.matchesWithField(tab?.generalDirectorOperation)
      : false;
  });


  private reportType = toSignal(this._store.reportType$);

  ic = toSignal(this._store.equipment$);

  criticality = computed(() => this.ic()?.consequenceRanking);

  private async isCurrentUserAdmin(): Promise<boolean> {
    return false;
  }

  cloneReport = computed(() => {
    const roles = this.roles();
    const isAdmin = this._authService.isAdmin();
    const isCreator = this._authService.isCreator();

    return roles ? isAdmin || isCreator : false;
  })

  // IsButtonEnabled4SubmitforReview
  draftForReview = computed(() => {
    const tab = this.approvalFlowState();
    const isAreaLeadValid = this.isAreaLeadValid();
    const isInitiatorValid = this.isInitiatorValid()
    // const stageField = this._store.stageField()
    const hasAreaLeadApproved = this.hasAreaLeadApproved();
    const state = this.currentApprovalState();

    if (
      tab?.id && tab?.areaLeadInspEng != null && !hasAreaLeadApproved &&
      (state === ApprovalState.Draft || state === ApprovalState.DraftedForReview) &&
      (
        this.reportType() === ReportType.IntegrityThreat ||
        this.reportType() === ReportType.ReliabilityThreat ||
        this.reportType() === ReportType.IntegrityDeficiency ||
        this.reportType() === ReportType.TaskDeferral ||
        this.reportType() === ReportType.ReliabilityDeficiency
      )
    ) {
      return state === ApprovalState.Draft && isInitiatorValid || state === ApprovalState.DraftedForReview && isAreaLeadValid || this.isAdmin;
    }
    else if (tab?.id && state === ApprovalState.Draft) {
      return isInitiatorValid || this.isAdmin;
    }
    else {
      return false;
    }
  });

  planConfirmed = computed(() => {
    const tab = this.approvalFlowState();
    const isAreaLeadValid = this.isAreaLeadValid();
    const isSrOpsConsultantValid = this.isSrOpsConsultantValid();
    const isInspEngSupervisorValid = this.isInspEngSupervisorValid();
    const state = this.currentApprovalState();

    if (
      this.reportType() === ReportType.AnomalyReport &&
      (state === ApprovalState.DraftedForReview)
    ) {
      return isAreaLeadValid || this.isAdmin;
    } else if (
      (this.reportType() === ReportType.IntegrityThreat || this.reportType() === ReportType.ReliabilityThreat) &&
      state === ApprovalState.SubmittedToOpsAdvisor
    ) {
      return isSrOpsConsultantValid || this.isAdmin;
    } else if (
      (this.reportType() === ReportType.IntegrityDeficiency ||
        this.reportType() === ReportType.ReliabilityDeficiency) &&
      state === ApprovalState.RiskEvaluationScored
    ) {
      return isSrOpsConsultantValid || this.isAdmin;
    } else if (
      this.reportType() === ReportType.TaskDeferral &&
      state === ApprovalState.RiskEvaluationScored
    ) {
      return isSrOpsConsultantValid || this.isAdmin;
    }

    // else if (
    //   this.reportType() === ReportType.ReliabilityThreat &&
    //   state === ApprovalState.DraftedForReview
    // ) {
    //   if (isSrOpsConsultantValid) {
    //     if (tab?.areaLeadInspEng != null) {
    //       return false;
    //     } else {
    //       return isSrOpsConsultantValid || this.isAdmin; // Need to check details
    //     }
    //   }
    // }

    return false;
  });

  requestRIPT = computed(() => {
    const isAreaLeadValid = this.isAreaLeadValid();
    const state = this.currentApprovalState();
    const approvalData = this.approvalFlowState();

    if (this.reportType() === ReportType.AnomalyReport && state === ApprovalState.DraftedForReview && !approvalData?.isRiptRequested) {
      return isAreaLeadValid || this.isAdmin;
    }

    return false;
  });


  // Submit for review - IsButtonEnabled4Submit_1
  submitForSrOps = computed(() => {
    const tab = this.approvalFlowState();
    const state = this.currentApprovalState();

    const isInspEngSupervisorValid = this.isInspEngSupervisorValid();
    const isAreaLeadInspEngValid = this.isAreaLeadValid();

    const hasAreaLeadApproved = this.hasAreaLeadApproved();

    // const stageField = this._store.stageField()

    // if (
    //   this.reportType() === ReportType.ReliabilityThreat &&
    //   state === ApprovalState.DraftedForReview &&
    //   (  isInspEngSupervisorValid)
    // ) {
    //   return isInspEngSupervisorValid || this.isAdmin;
    // } else
    if (
      (this.reportType() === ReportType.IntegrityThreat ||
        this.reportType() === ReportType.ReliabilityThreat ||
        this.reportType() === ReportType.ReliabilityDeficiency ||
        this.reportType() === ReportType.TaskDeferral ||
        this.reportType() === ReportType.IntegrityDeficiency) &&
      state === ApprovalState.DraftedForReview &&
      ((tab?.areaLeadInspEng != null && hasAreaLeadApproved) || (tab?.areaLeadInspEng == null && !hasAreaLeadApproved))
    ) {
      return isInspEngSupervisorValid || this.isAdmin;
    } else {
      return false;
    }
  });

  riptScored = computed(() => {
    const isInitiatorValid = this.isInitiatorValid();

    if (
      (this.reportType() === ReportType.ReliabilityThreat ||
        this.reportType() === ReportType.IntegrityThreat ||
        this.reportType() === ReportType.ReliabilityDeficiency ||
        this.reportType() === ReportType.IntegrityDeficiency) &&
      this.currentApprovalState() === ApprovalState.PlanConfirmed
    ) {
      return isInitiatorValid || this.isRiptRisk || this.isAdmin;
    }

    return false;
  });

  riptScoredSkipped = computed(() => {
    const isAreaLeadInspEngValid = this.isAreaLeadValid();
    const state = this.currentApprovalState();

    if (this.reportType() === ReportType.AnomalyReport && state == ApprovalState.PlanConfirmed && (isAreaLeadInspEngValid || this.isAdmin)) {
      return true;
    }
    else {
      return false;
    }
  });

  riskEvaluationScored = computed(() => {
    // const tab = this.approvalFlowState();
    const state = this.currentApprovalState();
    const isSrOpsConsultantValid = this.isSrOpsConsultantValid();

    if (
      (this.reportType() === ReportType.ReliabilityDeficiency ||
        this.reportType() === ReportType.IntegrityDeficiency) &&
      state === ApprovalState.SubmittedToOpsAdvisor
    ) {
      return (isSrOpsConsultantValid || this.isAdmin || this.isRiptRisk);
    }

    if (
      this.reportType() === ReportType.TaskDeferral &&
      state === ApprovalState.SubmittedToOpsAdvisor
    ) {
      return (isSrOpsConsultantValid || this.isAdmin || this.isRiptRisk);
    }

    return false;
  });

  v_n_vCompleted = computed(() => {
    // const tab = this.approvalFlowState();
    const state = this.currentApprovalState();
    const isInitiatorValid = this.isInitiatorValid();

    if (
      (this.reportType() === ReportType.AnomalyReport ||
        this.reportType() === ReportType.IntegrityDeficiency ||
        this.reportType() === ReportType.IntegrityThreat ||
        this.reportType() === ReportType.ReliabilityDeficiency ||
        this.reportType() === ReportType.ReliabilityThreat) &&
      state === ApprovalState.ResolutionPlanCompleted
    ) {
      return isInitiatorValid || this.isAdmin;
    }

    return false;
  });

  closed = computed(() => {
    const state = this.currentApprovalState();
    const isThreatOwnerValid = this.isThreatOwnerValid();
    const isInitiatorValid = this.isInitiatorValid();
    const isAIOwner = this._common_scripts.isAIOwner();

    // AI Owner cannot close reports
    if (isAIOwner) {
      return false;
    }

    if (this.reportType() !== ReportType.TaskDeferral && this.reportType() !== ReportType.AnomalyReport && state === ApprovalState.ValidationAndVerificationCompleted) {
      return isThreatOwnerValid || this.isAdmin;
    } else if (this.reportType() === ReportType.AnomalyReport && state === ApprovalState.ValidationAndVerificationCompleted) {
      return isInitiatorValid || this.isAdmin;
    } else if (this.reportType() === ReportType.TaskDeferral && state === ApprovalState.ResolutionPlanCompleted) {
      return isThreatOwnerValid || this.isAdmin;
    }

    return false;
  });

  cancelled = computed(() => {
    const isSrOpsConsultantValid = this.isSrOpsConsultantValid();
    const isThreatOwnerValid = this.isThreatOwnerValid();
    const state = this.currentApprovalState();
    const isAIOwner = this._common_scripts.isAIOwner();

    // AI Owner cannot perform this action
    if (isAIOwner) {
      return false;
    }

    if (state === ApprovalState.OutOfService) {
      return isThreatOwnerValid || this.isAdmin;
    }

    if (state !== ApprovalState.Closed && state !== ApprovalState.Cancelled) {
      return isThreatOwnerValid || isSrOpsConsultantValid || this.isAdmin;
    }
    return false;
  });

  reactivate = computed(() => {
    if (this._common_scripts.isAIOwner()) {
      return false;
    }

    return this.currentApprovalState() === ApprovalState.OutOfService
      && (this.isThreatOwnerValid() || this.isAdmin);
  });

  endorsers = computed(() => {
    const authorizedUserEmail = this._userService.email()?.toLowerCase();
    const approvalState = this.approvalFlowState();

    const endorsers = [
      {
        email: approvalState?.additionalEndorser1?.toLowerCase(),
        endorsed: approvalState?.additionalEndorser1Endorsed,
      },
      {
        email: approvalState?.additionalEndorser2?.toLowerCase(),
        endorsed: approvalState?.additionalEndorser2Endorsed,
      },
      {
        email: approvalState?.additionalEndorser3?.toLowerCase(),
        endorsed: approvalState?.additionalEndorser3Endorsed,
      },
    ];

    const haveEndorsers = endorsers.some(item => item.email != undefined);
    const activeEndorsers = endorsers.filter(item => item.email != undefined);
    const allApproved = activeEndorsers.every(item => item.endorsed);
    
    const isEndorser1Active = !endorsers[0].endorsed && authorizedUserEmail === endorsers[0].email && endorsers[0].email != undefined;
    const isEndorser2Active = endorsers[0].endorsed && !endorsers[1].endorsed && authorizedUserEmail === endorsers[1].email && endorsers[1].email != undefined;
    const isEndorser3Active = endorsers[1].endorsed && !endorsers[2].endorsed && authorizedUserEmail === endorsers[2].email && endorsers[2].email != undefined

    const isEndorserActive = haveEndorsers && ( isEndorser1Active || isEndorser2Active || isEndorser3Active );

    return { haveEndorsers, allApproved, isEndorserActive, isEndorser1Active, isEndorser2Active, isEndorser3Active, length: activeEndorsers.length };
  });

  planEndorsed = computed(() => {
    const state = this.currentApprovalState();
    const criticality = this.criticality();
    const endorsers = this.endorsers();
    const authLevel = this.authLevel();

    const hasLTAApproved = this.hasLTAApproved();
    const hasHCEPOApproved = this.hasHCEPOApproved();
    const hasAreaOpsApproved = this.hasAreaOpsApproved();
    const hasProdOpManagerApproved = this.hasProdOpManagerApproved();
    const hasGenManagerOperatorApproved = this.hasGenManagerOperatorApproved();

    const isLocalTechnicalAuthorityValid = this.isLocalTechnicalAuthorityValid();
    const isAssetTypeHCEPOValid = this.isAssetTypeHCEPOValid();
    const isAreaOpsSuperintendentValid = this.isAreaOpsSuperintendentValid();
    const isProductionOperationManagerValid = this.isProductionOperationManagerValid();
    const isGeneralManagerOperationValid = this.isGeneralManagerOperationValid();

    // const haveEndorsers = this.approvalFlowState

    if (
      (this.reportType() === ReportType.IntegrityDeficiency || this.reportType() === ReportType.ReliabilityDeficiency) &&
      (state == ApprovalState.RIPTScored || state == ApprovalState.PlanEndorsed)
    ) {
      if (criticality === 'IC3') {
        // Asset type HCEPO
        if (
          !hasHCEPOApproved && 
          (!endorsers.haveEndorsers || endorsers.allApproved) && 
          (isAssetTypeHCEPOValid || this.isAdmin)
        ) {
          return true;
        }
        // Area Ops Superintendant
        else if (hasHCEPOApproved && !hasAreaOpsApproved && (isAreaOpsSuperintendentValid || this.isAdmin)) {
          return true;
        }
        // Endorsers
        else if (!hasHCEPOApproved && endorsers.haveEndorsers && endorsers.isEndorserActive) {
          return true;
        }
        else {
          return false;
        }
      } else if (criticality === 'IC2' || criticality === 'AC2') {
        // LTA
        if (!hasLTAApproved && (isLocalTechnicalAuthorityValid || this.isAdmin)) {
          return true;
        }
        // Asset type HCEPO
        else if (
          hasLTAApproved && !hasHCEPOApproved && 
          ( !endorsers.haveEndorsers || endorsers.allApproved ) && 
          (isAssetTypeHCEPOValid || this.isAdmin)) {
          return true;
        }
        /**
         * Endorsers
         * check endorser for email matching
         */
        else if (!hasHCEPOApproved && endorsers.isEndorserActive) {
          return true;
        }
        // Area Ops Superintendant
        else if (hasLTAApproved && hasHCEPOApproved && !hasAreaOpsApproved && (isAreaOpsSuperintendentValid || this.isAdmin)) {
          return true;
        }
        // Production Operation Manager
        else if (hasLTAApproved && hasHCEPOApproved && hasAreaOpsApproved && !hasProdOpManagerApproved && (isProductionOperationManagerValid || this.isAdmin)) {
          return true;
        }
        else {
          return false;
        }
      } else if (criticality === 'IC1' || criticality === 'AC1') {
        // LTA
        if (!hasLTAApproved && (isLocalTechnicalAuthorityValid || this.isAdmin)) {
          return true;
        }
        // Asset type HCEPO
        else if (
          hasLTAApproved && !hasHCEPOApproved &&
          (
            !endorsers.haveEndorsers ||  // HCEPO did not added any endorsers
            endorsers.allApproved
          ) &&
          (isAssetTypeHCEPOValid || this.isAdmin)) {
          return true;
        }
        // Area Ops Superintendant
        else if (hasLTAApproved && hasHCEPOApproved && !hasAreaOpsApproved && (isAreaOpsSuperintendentValid || this.isAdmin)) {
          return true;
        }
        // Production Operation Manager
        else if (hasLTAApproved && hasHCEPOApproved && hasAreaOpsApproved && !hasProdOpManagerApproved && (isProductionOperationManagerValid || this.isAdmin)) {
          return true;
        }
        // General Manager Operation
        else if (hasLTAApproved && hasHCEPOApproved && hasAreaOpsApproved && hasProdOpManagerApproved && !hasGenManagerOperatorApproved && (isGeneralManagerOperationValid || this.isAdmin)) {
          return true;
        }
        // Endorsers
        else if (hasLTAApproved && !hasHCEPOApproved && endorsers.isEndorserActive) {
          return true;
        }
        else {
          return false;
        }
      } else {
        return false;
      }

    } else if (
      (this.reportType() === ReportType.IntegrityThreat || this.reportType() === ReportType.ReliabilityThreat)
    ) {
      if (criticality && ['IC1', 'IC2', 'IC3', 'AC1', 'AC2'].includes(criticality)) {
        if (
          !hasHCEPOApproved &&
          this.abcPrioritization() === 'A' &&
          (!endorsers.haveEndorsers && state === ApprovalState.RIPTScored || endorsers.allApproved && state === ApprovalState.PlanEndorsed) &&
          (isAssetTypeHCEPOValid || this.isAdmin)
        ) {
          return true;
        } else if (!hasHCEPOApproved && endorsers.haveEndorsers && endorsers.isEndorserActive) {
          return true;
        } else {
          return false;
        }
      } else {
        return false;
      }
    } else if (this.reportType() === ReportType.TaskDeferral && (state === ApprovalState.PlanConfirmed || state === ApprovalState.PlanEndorsed)) { // need to check risk ev scored
      // 584
      if (criticality && ['IC1', 'IC2', 'IC3', 'AC1', 'AC2'].includes(criticality)) {
        if (
          !hasHCEPOApproved && 
          (!endorsers.haveEndorsers || endorsers.allApproved) && 
          (isAssetTypeHCEPOValid || this.isAdmin)
        ) {
          return true;
        } else if (!hasHCEPOApproved && endorsers.isEndorserActive) {
          return true;
        } else if (authLevel === 'A') {
          return false;
        } else if (authLevel === 'B') {
          if (hasHCEPOApproved && !hasAreaOpsApproved && (isAreaOpsSuperintendentValid || this.isAdmin)) {
            return true;
          } else {
            return false;
          }
        } else if (authLevel === 'C') {
          if (hasHCEPOApproved && !hasAreaOpsApproved && (isAreaOpsSuperintendentValid || this.isAdmin)) {
            return true;
          } else if (hasAreaOpsApproved && !hasProdOpManagerApproved && (isProductionOperationManagerValid || this.isAdmin)) {
            return true;
          } else {
            return false
          }
        } else if (authLevel === 'D') {
          if (hasHCEPOApproved && !hasAreaOpsApproved && (isAreaOpsSuperintendentValid || this.isAdmin)) {
            return true;
          } else if (hasAreaOpsApproved && !hasProdOpManagerApproved && (isProductionOperationManagerValid || this.isAdmin)) {
            return true;
          } else if (hasAreaOpsApproved && hasProdOpManagerApproved && !hasGenManagerOperatorApproved && (isGeneralManagerOperationValid || this.isAdmin)) {
            return true;
          } else {
            return false
          }
        } else {
          return false;
        }
      } else {
        if (authLevel === 'A') {
          return false;
        } else if (authLevel === 'B') {
          if (!hasAreaOpsApproved && (isAreaOpsSuperintendentValid || this.isAdmin)) {
            return true;
          } else {
            return false;
          }
        } else if (authLevel === 'C') {
          if (!hasAreaOpsApproved && (isAreaOpsSuperintendentValid || this.isAdmin)) {
            return true;
          } else if (hasAreaOpsApproved && !hasProdOpManagerApproved && (isProductionOperationManagerValid || this.isAdmin)) {
            return true
          } else {
            return false;
          }
        } else if (authLevel === 'D') {
          if (!hasAreaOpsApproved && (isAreaOpsSuperintendentValid || this.isAdmin)) {
            return true;
          } else if (hasAreaOpsApproved && !hasProdOpManagerApproved && (isProductionOperationManagerValid || this.isAdmin)) {
            return true
          } else if (hasAreaOpsApproved && hasProdOpManagerApproved && !hasGenManagerOperatorApproved && (!isGeneralManagerOperationValid || this.isAdmin)) {
            return true;
          } else {
            return false
          }
        } else {
          return false;
        }
      }
    } else {
      return false;
    }
  });

  addNewEndorsers = computed(() => {
    const state = this.currentApprovalState();
    const criticality = this.criticality();
    const reportType = this.reportType();
    const ript = this.abcPrioritization();
    const endorsers = this.endorsers();
    const hasLTAApproved = this.hasLTAApproved();
    const hasHCEPOApproved = this.hasHCEPOApproved();
    // const hasHCEPOApproved = false;

    const isAssetTypeHCEPOValid = this.isAssetTypeHCEPOValid();
    const isHCE = criticality ? ['IC1', 'IC2', 'IC3', 'AC1', 'AC2'].includes(criticality) : false;

    if (endorsers.isEndorserActive) {
      return false;
    }
    else if (
      (reportType === ReportType.ReliabilityDeficiency || reportType === ReportType.IntegrityDeficiency) &&
      (state == ApprovalState.PlanEndorsed && hasLTAApproved || criticality === 'IC3' && state == ApprovalState.RIPTScored) &&
      (endorsers.length < 3) && !hasHCEPOApproved && (isAssetTypeHCEPOValid || this.isAdmin)
    ) {
      return true;
    }
    else if (
      isHCE &&
      ript === 'A' &&
      (endorsers.length < 3) &&
      !hasHCEPOApproved &&
      state === ApprovalState.RIPTScored &&
      (isAssetTypeHCEPOValid || this.isAdmin) &&
      (reportType === ReportType.ReliabilityThreat || reportType === ReportType.IntegrityThreat)
    ) {
      return true;
    } else if (
      isHCE &&
      (endorsers.length < 3) &&
      !hasHCEPOApproved &&
      state === ApprovalState.PlanConfirmed &&
      (isAssetTypeHCEPOValid || this.isAdmin) &&
      reportType === ReportType.TaskDeferral
    ) {
      return true;
    }

    return false;
  });

  planApproved = computed(() => {
    // const tab = this.approvalFlowState();
    const state = this.currentApprovalState();
    const reportType = this.reportType();
    const authLevel = this.authLevel();
    const criticality = this.criticality();
    const abcPrioritization = this.abcPrioritization();

    const hasLTAApproved = this.hasLTAApproved();
    const hasHCEPOApproved = this.hasHCEPOApproved();
    const hasAreaOpsApproved = this.hasAreaOpsApproved();
    const hasDirectorApproved = this.hasDirectorApproved();
    const hasProdOpManagerApproved = this.hasProdOpManagerApproved();
    const hasGenManagerOperatorApproved = this.hasGenManagerOperatorApproved();

    // Ops Advisor (Owner of ITR/RTR) is resolved from the Ops Advisor cell
    const isOpsAdvisorValid = this.isSrOpsConsultantValid();
    const isInspEngSupervisorValid = this.isInspEngSupervisorValid();
    const isAreaOpsSuperintendentValid = this.isAreaOpsSuperintendentValid();
    const isProductionOperationManagerValid = this.isProductionOperationManagerValid();
    const isGeneralManagerOperationValid = this.isGeneralManagerOperationValid();
    const isGeneralDirectorOperationValid = this.isGeneralDirectorOperationValid();

    const isHCE = criticality && ['IC1', 'IC2', 'IC3', 'AC1', 'AC2'].includes(criticality);
    
    if ((reportType === ReportType.ReliabilityThreat || reportType === ReportType.IntegrityThreat) && (state === ApprovalState.RIPTScored || state === ApprovalState.PlanEndorsed)) {
      // Ops Advisor always has the right to approve at Plan Endorsed stage
      if (isOpsAdvisorValid && state === ApprovalState.PlanEndorsed) {
        return true;
      }
      if (isHCE) {
        if (abcPrioritization === 'A' && hasHCEPOApproved && (isAreaOpsSuperintendentValid || this.isAdmin || isOpsAdvisorValid)) {
          return true;
        } else if (abcPrioritization !== 'A' && (isAreaOpsSuperintendentValid || this.isAdmin || isOpsAdvisorValid)) {
          return true;
        } else {
          return false;
        }
      } else if (!isHCE && (isAreaOpsSuperintendentValid || this.isAdmin || isOpsAdvisorValid)) {
        return true;
      } else {
        return false;
      }
    } else if (reportType === ReportType.AnomalyReport && state === ApprovalState.PlanConfirmed && (isInspEngSupervisorValid || this.isAdmin)) {
      return true;
    } else if ((reportType === ReportType.IntegrityDeficiency || reportType === ReportType.ReliabilityDeficiency) && (state == ApprovalState.RIPTScored || state == ApprovalState.PlanEndorsed)) {
      if (criticality === 'IC3' && hasAreaOpsApproved && (isProductionOperationManagerValid || this.isAdmin)) {
        return true;
      } else if (criticality === 'IC2' || criticality === 'AC2') {
        if (hasLTAApproved && hasAreaOpsApproved && hasProdOpManagerApproved && !hasGenManagerOperatorApproved && (isGeneralManagerOperationValid || this.isAdmin)) {
          return true;
        } else {
          return false;
        }
      } else if (criticality === 'IC1' || criticality === 'AC1') {
        if (hasLTAApproved && hasAreaOpsApproved && hasProdOpManagerApproved && hasGenManagerOperatorApproved && !hasDirectorApproved && (isGeneralDirectorOperationValid || this.isAdmin)) {
          return true;
        } else {
          return false;
        }
      } else if (!isHCE) {
        if (isAreaOpsSuperintendentValid || this.isAdmin) {
          return true;
        } else {
          return false;
        }
      } else {
        return false;
      }
    } else if (reportType === ReportType.TaskDeferral && (state === ApprovalState.PlanConfirmed || state === ApprovalState.PlanEndorsed)) {
      if (isHCE) {
        if (authLevel === 'A' && hasHCEPOApproved && !hasAreaOpsApproved && (isAreaOpsSuperintendentValid || this.isAdmin)) {
          return true;
        } else if (authLevel === 'B' && hasHCEPOApproved && hasAreaOpsApproved && !hasProdOpManagerApproved && (isProductionOperationManagerValid || this.isAdmin)) {
          return true;
        } else if (authLevel === 'C' && hasHCEPOApproved && hasAreaOpsApproved && hasProdOpManagerApproved && !hasGenManagerOperatorApproved && (isGeneralManagerOperationValid || this.isAdmin)) {
          return true;
        } else if (authLevel === 'D' && hasHCEPOApproved && hasAreaOpsApproved && hasProdOpManagerApproved && hasGenManagerOperatorApproved && !hasDirectorApproved && (isGeneralDirectorOperationValid || this.isAdmin)) {
          return true;
        } else {
          return false;
        }
      } else if (!isHCE) {
        if (!hasAreaOpsApproved && (isAreaOpsSuperintendentValid || this.isAdmin)) {
          return true;
        }
        else {
          return false;
        }
      } else {
        return false;
      }
    } else {
      return false;
    }
  });

  outOfService = computed(() => {
    const state = this.currentApprovalState();
    const isThreatOwnerValid = this.isThreatOwnerValid();
    const isAIOwner = this._common_scripts.isAIOwner();

    // AI Owner cannot perform this action
    if (isAIOwner) {
      return false;
    }

    if (state !== ApprovalState.Closed && state !== ApprovalState.OutOfService && state !== ApprovalState.Cancelled) {
      return isThreatOwnerValid || this.isAdmin
    }
    return false;
  });

  recycle = computed(() => {
    const isThreatOwnerValid = this.isThreatOwnerValid();
    const isInitiatorValid = this.isInitiatorValid();
    const isAIOwner = this._common_scripts.isAIOwner();

    const planApproved = this.planApproved();
    const planEndorsed = this.planEndorsed();
    const riptScored = this.riptScored();
    const riskEvaluationScored = this.riskEvaluationScored();
    const planConfirmed = this.planConfirmed();
    const submitForSrOps = this.submitForSrOps();
    const draftForReview = this.draftForReview();
    const v_n_vCompleted = this.v_n_vCompleted();
    const state = this.currentApprovalState();

    // AI Owner cannot perform this action
    if (isAIOwner) {
      return false;
    }

    if (state === ApprovalState.Closed || state === ApprovalState.OutOfService || state === ApprovalState.Cancelled)
      return false;
    else {
      return (
        (this.isAdmin ||
          draftForReview ||
          submitForSrOps ||
          planConfirmed ||
          riskEvaluationScored ||
          riptScored ||
          planEndorsed ||
          planApproved ||
          v_n_vCompleted ||
          isInitiatorValid ||
          isThreatOwnerValid) && (state !== ApprovalState.Draft)
      );
    }
  });

  updateRIPT = computed(() => {
    const isInitiatorValid = this.isInitiatorValid();
    const isThreatOwnerValid = this.isThreatOwnerValid();
    const state = this.currentApprovalState();
    const reportType = this.reportType();

    if (reportType === ReportType.AnomalyReport && this.ript()?.riptUrl != null &&
      (state === ApprovalState.PlanEndorsed ||
        // state === ApprovalState.RIPTScoredSkipped ||
        state === ApprovalState.PlanApproved ||
        state === ApprovalState.ResolutionPlanCompleted ||
        state === ApprovalState.ValidationAndVerificationCompleted)) {

      return isInitiatorValid || isThreatOwnerValid || this.isRiptRisk || this.isAdmin;

    }
    else if (reportType != ReportType.AnomalyReport && reportType != ReportType.TaskDeferral &&
      (state === ApprovalState.PlanEndorsed ||
        state === ApprovalState.RIPTScored ||
        state === ApprovalState.PlanApproved ||
        state === ApprovalState.ResolutionPlanCompleted ||
        state === ApprovalState.ValidationAndVerificationCompleted)) {

      return isInitiatorValid || isThreatOwnerValid || this.isRiptRisk || this.isAdmin;
    }
    else {
      return false;
    }



  });
  reportTypeChange = computed(() => {
    const isInitiatorValid = this.isInitiatorValid();
    const isThreatOwnerValid = this.isThreatOwnerValid();
    const state = this.currentApprovalState();
    const reportType = this.reportType();

    if (
      state !== ApprovalState.PlanApproved &&
      state !== ApprovalState.ResolutionPlanCompleted &&
      state !== ApprovalState.ValidationAndVerificationCompleted &&
      state !== ApprovalState.Closed &&
      state !== ApprovalState.OutOfService &&
      state !== ApprovalState.Cancelled &&
      reportType !== ReportType.TaskDeferral
    ) {
      return isInitiatorValid || isThreatOwnerValid || this.isAdmin;
    } else {
      return false;
    }

  });
}
